import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import { proService, stripeService } from '../services';
import ProSubscribeModal from '../components/ProSubscribeModal';
import { getPurchaseMode, PURCHASE_IN_APP, PURCHASE_LINK_OUT } from '../utils/externalPurchase';
import { SPECIES } from '../theme/species';
import { BRAND_DOMAIN, BRAND_URL } from '../utils/brand';
import { bookingWhen, offerSessions } from '../utils/proBookingTime';

const TIER_LABEL = { founding: 'Founding · free', standard: 'Standard', party_for_good: 'Party for good · free' };

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
/** The first session's date + start (– end) time; a package says how many sessions follow. */
export const deskWhen = (o) => {
  const [first] = offerSessions(o);
  const line = first ? bookingWhen(first) : null;
  if (!line) return null;
  return o.sessionCount > 1 ? `${line} · ${o.sessionCount} sessions` : line;
};

const STATUS_COPY = {
  sent: 'New', countered: 'Countered', accepted: 'Agreed', confirmed: 'Booked',
  declined: 'Declined', expired: 'Expired', canceled: 'Canceled',
};

/**
 * ProDeskPage — where booking offers live (6b). Two tabs: offers you've
 * received across your Pro pages (owner) and offers you've sent (buyer).
 */
export default function ProDeskPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { user, logout } = useAuth();
  const pink = SPECIES.partyStarter;

  const role = profile?.role || user?.role;
  const isAdmin = role === 'admin' || role === 'superadmin';
  const { showConfirm, showAlert } = useDialog();
  const [tab, setTab] = useState('incoming');
  const [incoming, setIncoming] = useState([]);
  const [mine, setMine] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [sub, setSub] = useState(null);
  const [showSubscribe, setShowSubscribe] = useState(false);
  // Where this build is allowed to sell the upgrade. See utils/externalPurchase.
  const [purchaseMode, setPurchaseMode] = useState(PURCHASE_IN_APP);
  useEffect(() => {
    let cancelled = false;
    getPurchaseMode().then((m) => { if (!cancelled) setPurchaseMode(m); });
    return () => { cancelled = true; };
  }, []);
  const [loading, setLoading] = useState(true);
  const [payoutBusy, setPayoutBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [inc, my, earn, s] = await Promise.all([
      proService.incomingOffers(), proService.myOffers(), proService.getEarnings(), proService.getSubscription(),
    ]);
    if (inc.success) setIncoming(inc.data || []);
    if (my.success) setMine(my.data || []);
    if (earn.success) setEarnings(earn.data);
    if (s.success) setSub(s.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelSub = async () => {
    const ok = await showConfirm({ title: 'CANCEL SUBSCRIPTION', message: 'Your Pro page stays active until the end of the period you’ve paid for. Cancel it?', confirmLabel: 'CANCEL SUB', variant: 'danger' });
    if (!ok) return;
    const r = await proService.cancelSubscription();
    if (r.success) { setSub(r.data); }
    else showAlert({ title: 'COULD NOT CANCEL', message: r.error || 'Try again.', type: 'error' });
  };

  const handlePayout = async () => {
    if (earnings?.hasPayoutAccount) { navigate('/wallet'); return; }
    setPayoutBusy(true);
    const r = await stripeService.startOnboarding('wallet');
    setPayoutBusy(false);
    if (r.success && r.data?.url) window.location.href = r.data.url;
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const list = tab === 'incoming' ? incoming : mine;
  const needsYou = (o) =>
    (tab === 'incoming' && ['sent', 'countered'].includes(o.status) && o.currentTurn === 'owner') ||
    (tab === 'mine' && (o.status === 'accepted' || (['sent', 'countered'].includes(o.status) && o.currentTurn === 'buyer')));

  const Row = ({ o }) => (
    <button
      onClick={() => navigate(`/pro/offer/${o.id}`)}
      className="flex w-full items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-mono text-sm font-bold">
            {tab === 'incoming' ? (o.buyerName || o.buyerEmail) : o.businessName}
          </span>
          {needsYou(o) && <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: pink }} />}
        </div>
        <div className="mt-0.5 font-mono text-[11px] text-gray-500">
          {STATUS_COPY[o.status] || o.status}{deskWhen(o) ? ` · ${deskWhen(o)}` : ''}
        </div>
      </div>
      <span className="flex-shrink-0 font-mono text-sm font-bold">{money(o.currentAmountCents)}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-inside-bg">
      <Navbar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />
      <div className="mx-auto max-w-2xl px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: pink }}>Pro</div>
          {isAdmin && (
            <button onClick={() => navigate('/pro/flags')}
              className="border-2 border-black px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-white">
              Disputes →
            </button>
          )}
        </div>
        <h1 className="mb-4 font-mono text-2xl font-bold uppercase tracking-wider">Booking desk</h1>

        {/* Subscription (6e) — only the standard tier owes; free tiers show nothing. */}
        {sub && !sub.isFreeTier && (
          <div className="mb-4 border-2 border-black bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500">Pro plan</div>
                <div className="mt-0.5 font-mono text-sm font-bold uppercase tracking-wider">
                  {money(sub.monthlyPriceCents)}<span className="text-xs text-gray-500"> / mo</span>
                </div>
              </div>
              <span className="border-2 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wide"
                style={sub.subscriptionState === 'active'
                  ? { borderColor: '#03FF00', color: '#0a0' }
                  : sub.subscriptionState === 'past_due'
                    ? { borderColor: '#FE0100', color: '#FE0100' }
                    : { borderColor: '#9ca3af', color: '#6b7280' }}>
                {sub.subscriptionState === 'active' ? 'Active' : sub.subscriptionState === 'past_due' ? 'Past due' : 'Not active'}
              </span>
            </div>
            {sub.subscriptionState === 'active' ? (
              <button onClick={cancelSub}
                className="mt-3 border-2 border-gray-300 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:border-black hover:text-black">
                Cancel subscription
              </button>
            ) : purchaseMode === PURCHASE_IN_APP ? (
              <button onClick={() => setShowSubscribe(true)}
                className="mt-3 w-full border-2 border-black py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-black hover:opacity-90" style={{ background: pink }}>
                {sub.subscriptionState === 'past_due' ? 'Update payment →' : 'Start subscription →'}
              </button>
            ) : purchaseMode === PURCHASE_LINK_OUT ? (
              // US storefront only — Apple has permitted external purchase links
              // there since May 2025, with no commission.
              <a
                href={`${BRAND_URL}/pro/desk`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 block w-full border-2 border-black py-2 text-center font-mono text-[11px] font-bold uppercase tracking-widest text-black hover:opacity-90"
                style={{ background: pink }}
              >
                {sub.subscriptionState === 'past_due' ? 'Update payment →' : 'Activate on the web →'}
              </a>
            ) : (
              // Everywhere else: state the status and where activation happens.
              // Deliberately NOT a link or button — under Guideline 3.1.1 a call
              // to action toward an outside purchase is the violation, so this
              // stays inert text the user reads and acts on themselves.
              <p className="mt-3 border-2 border-gray-300 bg-gray-50 p-3 font-mono text-[11px] leading-relaxed text-gray-600">
                This Pro page isn&rsquo;t activated yet. Activation is handled on
                {BRAND_DOMAIN} &mdash; everything else here keeps working in the
                meantime.
              </p>
            )}
          </div>
        )}

        {/* Earnings + payout (6e) — only meaningful once the user has a Pro page. */}
        {earnings && earnings.bookingCount > 0 && (
          <div className="mb-4 border-2 border-black bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500">Your money</span>
              <span className="border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wide" style={{ borderColor: pink, color: pink }}>
                {TIER_LABEL[earnings.pricingTier] || earnings.pricingTier}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="font-mono text-lg font-bold">{money(earnings.availableBalanceCents)}</div>
                <div className="font-mono text-[9px] uppercase tracking-wide text-gray-500">Available</div>
              </div>
              <div>
                <div className="font-mono text-lg font-bold text-gray-400">{money(earnings.escrowHeldCents)}</div>
                <div className="font-mono text-[9px] uppercase tracking-wide text-gray-500">In escrow</div>
              </div>
              <div>
                <div className="font-mono text-lg font-bold">{money(earnings.lifetimeEarnedCents)}</div>
                <div className="font-mono text-[9px] uppercase tracking-wide text-gray-500">Earned</div>
              </div>
            </div>
            <button onClick={handlePayout} disabled={payoutBusy}
              className="mt-3 w-full border-2 border-black py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-50" style={{ background: pink }}>
              {payoutBusy ? '…' : earnings.hasPayoutAccount ? 'Cash out →' : 'Set up payout →'}
            </button>
          </div>
        )}

        <div className="mb-4 flex border-2 border-black">
          {[['incoming', 'Received'], ['mine', 'Sent']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className="flex-1 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-colors"
              style={tab === key ? { background: '#000', color: '#fff' } : { background: '#fff', color: '#000' }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="border-2 border-black bg-white p-10"><LoadingSpinner label="Loading…" /></div>
        ) : list.length === 0 ? (
          <div className="border-2 border-black bg-white px-4 py-10 text-center font-mono text-xs text-gray-500">
            {tab === 'incoming' ? 'No offers yet. Share your Pro page to start getting booked.' : "You haven't made any offers yet."}
          </div>
        ) : (
          <div className="border-2 border-black bg-white">
            {list.map((o) => <Row key={o.id} o={o} />)}
          </div>
        )}
      </div>

      {sub && (
        <ProSubscribeModal
          isOpen={showSubscribe}
          onClose={() => setShowSubscribe(false)}
          monthlyPriceCents={sub.monthlyPriceCents}
          email={profile?.email}
          name={profile?.displayName}
          onDone={(s) => setSub(s)}
        />
      )}
    </div>
  );
}
